import React, { useEffect, useMemo, useState } from "react";
import { FaApple, FaChrome, FaGooglePlay, FaQrcode } from "react-icons/fa";
import { QRCode } from "react-qrcode-logo";
import { appPublicService } from "../../services/appPublicService";
import logo from "../../assets/imgs/apple-touch-icon.png";
import playStoreIcon from "../../assets/imgs/playstore.png";
import appStoreIcon from "../../assets/imgs/apple.png";

const DownloadPage = () => {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await appPublicService.getAppLinks();
        if (!mounted) return;
        setLinks(res.data?.links || null);
      } catch {
        // fall back to static defaults below
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const resolved = useMemo(() => {
    const fallbackDownloadUrl = `${window.location.origin}/download`;
    return {
      androidUrl:
        links?.androidUrl ||
        "https://play.google.com/store/apps/details?id=com.jhonks.jhonksmobile",
      iosUrl: links?.iosUrl || "https://www.apple.com/app-store/",
      webUrl: links?.webUrl || "/",
      downloadUrl: "https://jhonks.com/download" || fallbackDownloadUrl,
    };
  }, [links]);

  return (
    <div className="w-full">
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-green-50 via-white to-emerald-50 shadow-sm">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-green-200/40 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-200/30 blur-3xl" />
        </div>

        <div className="relative px-6 py-10 sm:px-10 sm:py-14">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_.8fr] lg:items-start">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/70 shadow-sm ring-1 ring-gray-200">
                  <img src={logo} alt="Jhonks" className="h-8 w-8 object-contain" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-700">Jhonks</p>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Download the Jhonks app
                  </h1>
                </div>
              </div>

              <p className="mt-4 max-w-2xl text-gray-600 leading-relaxed">
                Get the fastest experience on mobile, or continue right here on the web. Choose your
                platform below.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <a
                  href={resolved.androidUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between rounded-xl bg-green-600 px-5 py-4 text-white shadow-sm transition-colors hover:bg-green-700"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/15">
                      <FaGooglePlay className="text-xl" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm text-white/80">Android</p>
                      <p className="font-semibold">Get it on Google Play</p>
                    </div>
                  </div>
                  <img
                    src={playStoreIcon}
                    alt="Google Play"
                    className="h-7 w-7 object-contain opacity-90 group-hover:opacity-100"
                  />
                </a>

                <a
                  href={resolved.iosUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between rounded-xl bg-gray-900 px-5 py-4 text-white shadow-sm transition-colors hover:bg-black"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/15">
                      <FaApple className="text-2xl" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm text-white/80">iPhone</p>
                      <p className="font-semibold">Download on the App Store</p>
                    </div>
                  </div>
                  <img
                    src={appStoreIcon}
                    alt="App Store"
                    className="h-7 w-7 object-contain opacity-90 group-hover:opacity-100"
                  />
                </a>
              </div>

              <div className="mt-3">
                <a
                  href={resolved.webUrl}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white/80 px-5 py-4 font-semibold text-gray-900 shadow-sm transition-colors hover:bg-white"
                >
                  <FaChrome className="text-lg text-gray-700" />
                  Continue on web
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 ring-1 ring-gray-200">
                  <span className="h-2 w-2 rounded-full bg-green-600" />
                  Secure & fast
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 ring-1 ring-gray-200">
                  <span className="h-2 w-2 rounded-full bg-emerald-600" />
                  Works on all devices
                </span>
                {loading && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 ring-1 ring-gray-200">
                    <span className="h-2 w-2 rounded-full bg-gray-400 animate-pulse" />
                    Loading latest links…
                  </span>
                )}
              </div>
            </div>

            {/* QR section: medium+ screens only */}
            <div className="hidden md:block">
              <div className="rounded-2xl bg-white/75 p-6 shadow-sm ring-1 ring-gray-200">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-green-700">Scan on your phone</p>
                    <h2 className="mt-1 text-lg font-bold text-gray-900">
                      Open this download page instantly
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                      Point your camera at the QR code to open this exact page on mobile.
                    </p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 ring-1 ring-green-100">
                    <FaQrcode className="text-xl text-green-700" />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-center">
                  <div className="rounded-2xl border-4 border-green-600 bg-white p-4 shadow-sm">
                    <QRCode
                      value={resolved.downloadUrl}
                      size={190}
                      quietZone={10}
                      eyeRadius={6}
                      ecLevel="H"
                      logoImage={logo}
                      logoWidth={44}
                      logoHeight={44}
                      removeQrCodeBehindLogo
                      qrStyle="squares"
                      fgColor="#0F172A"
                    />
                  </div>
                </div>

                <p className="mt-4 text-center text-xs text-gray-500">
                  Scans to{" "}
                  <span className="font-medium text-gray-700 break-all">{resolved.downloadUrl}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;

